import { GetterTree } from 'vuex'

import { Item, ItemContext } from '@/models'

import { orderByLocale } from '@/utils'

import { RootState } from '../../state'
import { MenuState } from '../state'

const getters: GetterTree<MenuState, RootState> = {
  /**
   * Returns an Item by its context, or null if not found.
   *
   * It throws if the Item is not loaded but its id is in an ItemGroup relation.
   *
   * @param context ItemContext to be found.
   */
  itemByContext: (state) => ({ id, itemGroupId }: ItemContext): Item | null => {
    const itemGroupItems = state.itemGroups.items[itemGroupId]

    if (itemGroupItems && itemGroupItems.ids.has(id)) {
      const item = state.items.elements.find((i) => i.id === id)

      if (item === undefined) {
        throw new Error(`Item "${id}" was not found, but it is in the ItemGroup "${itemGroupId}" relation`)
      }

      return item
    }

    return null
  },

  /**
   * Returns all Items in an ItemGroup.
   *
   * It throws if the Item is not loaded but its id is in an ItemGroup relation.
   *
   * @param itemGroupId ItemGroup id to search.
   */
  itemsByItemGroupId: (state) => (itemGroupId: number): Item[] => {
    const itemGroupItems = state.itemGroups.items[itemGroupId]

    if (itemGroupItems === undefined) {
      return []
    }

    const items: Item[] = []

    for (const id of itemGroupItems.ids) {
      const item = state.items.elements.find((i) => i.id === id)

      if (item === undefined) {
        throw new Error(`Item "${id}" was not found, but it is in the ItemGroup "${itemGroupId}" relation`)
      }

      items.push(item)
    }

    return items
  },

  /**
   * Returns Items in an ItemGroup ordered by name.
   *
   * @param itemGroupId ItemGroup id to search.
   */
  orderedItems: (_, getters) => (itemGroupId: number): Item[] => {
    return orderByLocale(getters.itemsByItemGroupId(itemGroupId), 'name')
  }
}

export default getters
